import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useToast from "~/components/ui/toast";
import { nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { deleteProject, leaveProject, mutateProjectList } from "~/services/projects";

interface UseProjectExitReturn {
  /** Archives the project (a soft delete an admin can restore). Owner-only. */
  handleArchive: () => Promise<void>;
  /** Drops the caller's own access, leaving the project in place. */
  handleLeave: () => Promise<void>;
}

/**
 * The two ways a project stops being yours — archiving it, and leaving it.
 * Both end on the projects list, since the page they ran from is gone.
 *
 * `handleLeave` rethrows so `LeaveProjectDialog` can show the reason in place:
 * the backend answers 409 when the caller is the project's only owner.
 */
export const useProjectExit = (projectId: string, projectName: string): UseProjectExitReturn => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const handleArchive = useCallback(async () => {
    try {
      await deleteProject(projectId);
      await mutateProjectList();
      showSuccess(`"${projectName}" archived`);
      navigate(nextgenProjectsListPath());
    } catch {
      showError("Failed to archive project. Please try again.");
    }
  }, [projectId, projectName, navigate, showSuccess, showError]);

  const handleLeave = useCallback(async () => {
    await leaveProject(projectId);
    await mutateProjectList();
    navigate(nextgenProjectsListPath());
  }, [projectId, navigate]);

  return { handleArchive, handleLeave };
};
